import { createContext } from '@shared/utils/createContext';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ValuePropCardDetails } from './ValuePropCard.types';
import { VALUE_PROP_CREDIT_CARD } from './constants';
import { shuffle } from 'lodash-es';
import { breakpointAliasPx } from '@withjoy/joykit/theme';
import { useLocation } from '@react-router';
import { useShopStorefrontContext } from '../../../ShopStorefrontProvider';
import { useProductCatalogAdsQuery } from '@graphql/generated';
import { useCatalogContext } from '../../../../state/Catalog';
import type { BrandPromo } from '@apps/registry/admin/components/BrandCard/BrandCard';

type ValuePropCardContext = {
  getValuePropCardSlots: () => Map<number, ValuePropCardDetails>; // slotIndex -> card details
  markCardAsViewed: (cardId: string) => void;
  selectedBrandPromo?: BrandPromo | null;
};

const getColumnsCount = () => {
  const viewportWidth = window.innerWidth;
  return viewportWidth < breakpointAliasPx.sm2 ? 2 : viewportWidth < breakpointAliasPx.lg ? 3 : 4;
};

const [Provider, useValuePropCardContext] = createContext<ValuePropCardContext>({ name: 'ValuePropCardProvider' });

interface ValuePropCardProviderProps extends Readonly<{}> {
  children: React.ReactNode;
}

const ValuePropCardProvider = (props: ValuePropCardProviderProps) => {
  const { children } = props;
  const location = useLocation();
  const { config } = useShopStorefrontContext();
  const newLocation = useMemo(() => (location.search ? `${location.pathname}${location.search}&setupCreditCard=true` : `${location.pathname}?setupCreditCard=true`), [location]);
  const { data } = useProductCatalogAdsQuery({
    batchMode: 'fast',
  });
  const { selectedCategory } = useCatalogContext();

  const selectedBrandPromo = useMemo(() => {
    return data?.productCatalogAds?.departmentBrandPromos?.find(depBrandPromo => depBrandPromo.department.id === selectedCategory?.id)?.productGrid;
  }, [data?.productCatalogAds?.departmentBrandPromos, selectedCategory?.id]);

  const isPaidAdsEnabled = !!selectedBrandPromo;

  const cards: ValuePropCardDetails[] = useMemo(() => {
    const defaultCards = config.valuePropCardsData;
    return [...defaultCards, { ...VALUE_PROP_CREDIT_CARD, link: newLocation }];
  }, [newLocation, config]);

  const [viewedCardsIds, setViewedCardsIds] = useState<string[]>([]);

  // read from local storage
  useEffect(() => {
    const stringifiedValuePropCards = localStorage.getItem('valuePropCardsState');
    if (!stringifiedValuePropCards) {
      return;
    }
    setViewedCardsIds(JSON.parse(stringifiedValuePropCards));
  }, []);

  // write to local storage
  useEffect(() => {
    localStorage.setItem('valuePropCardsState', JSON.stringify(viewedCardsIds));
  }, [viewedCardsIds]);

  const getValuePropCardSlots = useCallback(() => {
    const columnsCount = getColumnsCount();

    // we randomize the non viewed cards but not the viewed cards as the we want to keep the last viewed cards at the end
    const nonViewedCards = cards.map(card => card.id).filter(cardId => !viewedCardsIds.includes(cardId));
    const randomizedNonViewedCards = shuffle(nonViewedCards);
    const allCards = [...randomizedNonViewedCards, ...viewedCardsIds];

    const cardsSlots = new Map<number, ValuePropCardDetails>();
    const isEvenColumnsCount = columnsCount % 2 === 0;

    if (isPaidAdsEnabled) {
      cardsSlots?.set(isEvenColumnsCount ? 7 : 5, { id: 'brandCard' } as ValuePropCardDetails);
    }

    allCards.forEach(cardId => {
      const card = cards.find(card => card.id === cardId);
      if (card) {
        const lastSlot = Array.from(cardsSlots.entries())?.pop()?.[0];
        if (!lastSlot) {
          cardsSlots.set(isEvenColumnsCount ? 7 : 5, card);
        } else if (cardsSlots.size % 2 === 0) {
          cardsSlots.set(isEvenColumnsCount ? lastSlot + 22 : lastSlot + 31, card);
        } else {
          cardsSlots.set(isEvenColumnsCount ? lastSlot + 26 : lastSlot + 17, card);
        }
      }
    });

    return cardsSlots;
  }, [cards, isPaidAdsEnabled, viewedCardsIds]);

  const markCardAsViewed = useCallback(
    (cardId: string) => {
      let viewedCards = [...viewedCardsIds];

      if (!viewedCards.includes(cardId)) {
        viewedCards.push(cardId);
        // if all cards were viewed, reset the list and keep only the last viewed card to reduce the chances to see it again in the next slot
        if (viewedCards.length === cards.length) {
          const lastViewedCardId = viewedCards.pop();
          viewedCards = [lastViewedCardId as string];
        }
        setViewedCardsIds(viewedCards);
      }
    },
    [cards.length, viewedCardsIds],
  );

  return <Provider value={{ getValuePropCardSlots, markCardAsViewed, selectedBrandPromo }}>{children}</Provider>;
};

ValuePropCardProvider.displayName = 'ValueCardPropProvider';

export { ValuePropCardProvider, useValuePropCardContext };
